import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  NavArrowLeft,
  NavArrowDown,
  Search,
  Xmark,
} from 'iconoir-react';
import { PublicFooter } from '../components/PublicFooter';

interface HelpLink {
  label: string;
  to: string;
}

interface HelpQuestion {
  id: string;
  question: string;
  answer: string;
  link?: HelpLink;
}

interface HelpCategory {
  id: string;
  title: string;
  questions: HelpQuestion[];
}

const HELP_CATEGORIES: HelpCategory[] = [
  {
    id: 'premiers-pas',
    title: 'Premiers pas',
    questions: [
      {
        id: 'premiers-pas-1',
        question: 'Comment créer un compte Ventilio ?',
        answer:
          'Depuis la page d’accueil, cliquez sur « Commencer gratuitement », renseignez vos informations, choisissez le type de compte souhaité puis l’offre qui vous convient. Vous pouvez également vous inscrire avec Google ou Microsoft.',
      },
      {
        id: 'premiers-pas-2',
        question: 'Dois-je vérifier mon adresse email ?',
        answer:
          'Oui. Après votre inscription, un email de vérification vous est envoyé. Votre adresse doit être vérifiée avant de pouvoir accéder normalement à vos tableaux.',
      },
      {
        id: 'premiers-pas-3',
        question: 'Comment me connecter à mon compte ?',
        answer:
          'Depuis la page de connexion, saisissez votre adresse email et votre mot de passe. Vous pouvez également utiliser la connexion avec Google ou Microsoft si votre compte le permet.',
      },
      {
        id: 'premiers-pas-4',
        question: 'J’ai oublié mon mot de passe, que faire ?',
        answer:
          'Depuis la page de connexion, cliquez sur « Mot de passe oublié ? ». Indiquez votre adresse email : vous recevrez un lien vous permettant de définir un nouveau mot de passe.',
      },
    ],
  },

  {
    id: 'tableaux-et-cartes',
    title: 'Tableaux et cartes',
    questions: [
      {
        id: 'tableaux-1',
        question: 'Comment créer un tableau ?',
        answer:
          'Depuis votre tableau de bord, utilisez la zone de création d’un nouveau tableau, indiquez son nom puis validez. Vous pourrez ensuite y ajouter vos colonnes et vos cartes.',
      },
      {
        id: 'tableaux-2',
        question: 'Comment créer ou modifier une carte ?',
        answer:
          'Ajoutez une carte depuis une colonne, puis ouvrez-la pour modifier ses informations : titre, description, dates, étiquettes, membres assignés ou pièces jointes.',
      },
      {
        id: 'tableaux-3',
        question: 'Puis-je organiser mes colonnes librement ?',
        answer:
          'Oui. Vous pouvez créer, renommer et réorganiser les colonnes afin d’adapter chaque tableau à votre organisation et à votre manière de travailler.',
      },
      {
        id: 'tableaux-4',
        question: 'Comment archiver une carte et la retrouver ensuite ?',
        answer:
          'Une carte peut être archivée depuis ses actions. Les cartes archivées restent accessibles depuis la page dédiée aux archives du tableau et peuvent ainsi être retrouvées ultérieurement.',
      },
      {
        id: 'tableaux-5',
        question: 'Comment utiliser les étiquettes ?',
        answer:
          'Les étiquettes permettent de catégoriser visuellement vos cartes. Vous pouvez créer les étiquettes propres à un tableau, leur attribuer une couleur puis les appliquer aux cartes concernées.',
      },
      {
        id: 'tableaux-6',
        question: 'Puis-je joindre des fichiers à une carte ?',
        answer:
          'Oui. Vous pouvez ajouter des pièces jointes à vos cartes dans la limite de l’espace de stockage disponible avec votre offre.',
      },
    ],
  },

  {
    id: 'membres-et-invitations',
    title: 'Membres et invitations',
    questions: [
      {
        id: 'membres-1',
        question: 'Comment inviter quelqu’un sur un tableau ?',
        answer:
          'Depuis la gestion des membres du tableau, indiquez l’adresse email de la personne à inviter et sélectionnez son rôle. Une invitation lui sera envoyée par email.',
      },
      {
        id: 'membres-2',
        question: 'Puis-je partager un lien d’invitation ?',
        answer:
          'Oui. Un administrateur peut générer un lien d’invitation partageable pour un tableau. Ce lien peut également être désactivé à tout moment depuis la gestion des membres.',
      },
      {
        id: 'membres-3',
        question: 'Quels rôles existent sur un tableau ?',
        answer:
          'Ventilio distingue plusieurs rôles : propriétaire, administrateur, éditeur et visiteur. Les actions disponibles varient selon le rôle attribué à chaque membre.',
      },
      {
        id: 'membres-4',
        question: 'Un tableau peut-il être partagé avec plusieurs personnes ?',
        answer:
          'Oui. Plusieurs personnes peuvent participer au même tableau. Chacune accède aux fonctionnalités correspondant au rôle qui lui a été attribué.',
      },
    ],
  },

  {
    id: 'compte-et-abonnement',
    title: 'Compte et abonnement',
    questions: [
      {
        id: 'compte-1',
        question: 'Comment modifier mon profil ?',
        answer:
          'Depuis la gestion de votre compte, vous pouvez consulter et modifier les informations disponibles dans votre profil.',
      },
      {
        id: 'compte-2',
        question: 'Puis-je ajouter une photo de profil ?',
        answer:
          'Oui. Votre espace de compte vous permet d’ajouter une photo de profil ou de supprimer votre avatar actuel.',
      },
      {
        id: 'compte-3',
        question: 'Quelle est la différence entre l’offre Gratuit et l’offre Premium ?',
        answer:
          'Les offres Ventilio proposent des limites et avantages différents, notamment concernant le nombre de tableaux, les cartes, les membres ou le stockage. Consultez la présentation des tarifs pour comparer les offres disponibles.',
        link: {
          label: 'Voir les tarifs',
          to: '/#pricing',
        },
      },
      {
        id: 'compte-4',
        question: 'Que se passe-t-il si j’atteins les limites de mon offre ?',
        answer:
          'Lorsque vous atteignez ou dépassez une limite de votre offre, Ventilio vous en informe et vous indique les options disponibles depuis votre espace de compte.',
      },
      {
        id: 'compte-5',
        question: 'Où gérer mes documents et pièces jointes stockés ?',
        answer:
          'La gestion de votre compte comprend une section Documents qui vous permet de consulter les fichiers stockés et, lorsque cela est disponible, de les supprimer.',
      },
    ],
  },

  {
    id: 'problemes-frequents',
    title: 'Problèmes fréquents',
    questions: [
      {
        id: 'problemes-1',
        question: 'Je n’ai pas reçu mon email de vérification ou de réinitialisation',
        answer:
          'Vérifiez d’abord que l’adresse email renseignée est correcte, puis consultez vos courriers indésirables ou spams. Vous pouvez ensuite demander un nouvel envoi depuis la page concernée.',
      },
      {
        id: 'problemes-2',
        question: 'Mon lien d’invitation ne fonctionne pas',
        answer:
          'Le lien peut avoir été désactivé ou ne plus être valide. Demandez à l’administrateur ou au propriétaire du tableau de vous transmettre une nouvelle invitation.',
      },
      {
        id: 'problemes-3',
        question: 'J’ai oublié mon mot de passe',
        answer:
          'Utilisez le lien « Mot de passe oublié ? » depuis la page de connexion. Un email vous sera envoyé afin de réinitialiser votre mot de passe.',
      },
      {
        id: 'problemes-4',
        question: 'J’obtiens un message d’accès refusé sur un tableau',
        answer:
          'Cela peut se produire si vous ne disposez plus des droits nécessaires pour accéder au tableau, par exemple si votre rôle a été modifié ou si vous avez été retiré du tableau.',
      },
      {
        id: 'problemes-5',
        question: 'Je ne peux plus créer de tableau, de carte ou ajouter un membre',
        answer:
          'Vérifiez les limites associées à votre offre depuis votre espace de compte. Ventilio vous indique lorsque l’une de ces limites est atteinte.',
      },
    ],
  },

  {
    id: 'confidentialite-et-securite',
    title: 'Confidentialité et sécurité',
    questions: [
      {
        id: 'securite-1',
        question: 'Quelles sont les règles pour mon mot de passe ?',
        answer:
          'Votre mot de passe doit contenir au minimum 8 caractères. Il est enregistré de manière sécurisée et n’est jamais affiché en clair.',
      },
      {
        id: 'securite-2',
        question: 'Pourquoi dois-je vérifier mon adresse email ?',
        answer:
          'La vérification de votre adresse email permet de confirmer que vous avez bien accès à l’adresse utilisée lors de la création du compte et contribue à protéger votre accès à Ventilio.',
      },
      {
        id: 'securite-3',
        question: 'Comment sont utilisés les cookies sur Ventilio ?',
        answer:
          'Lors de votre visite, un bandeau vous permet de gérer les cookies non essentiels. Pour plus de détails, consultez notre Politique des cookies.',
        link: {
          label: 'Consulter la politique des cookies',
          to: '/cookies-policy',
        },
      },
      {
        id: 'securite-4',
        question: 'Où trouver les documents légaux ?',
        answer:
          'Les Conditions Générales d’Utilisation, les Conditions Générales de Vente, la Politique de confidentialité et la Politique des cookies sont accessibles depuis les pages légales de Ventilio.',
      },
    ],
  },
];

const normalize = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const HelpPage: React.FC = () => {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggleQuestion = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const filteredCategories = useMemo(() => {
    const term = normalize(query.trim());

    if (!term) {
      return HELP_CATEGORIES;
    }

    return HELP_CATEGORIES
      .map((category) => {
        const categoryMatches = normalize(category.title).includes(term);

        const questions = category.questions.filter((question) => {
          const linkText = question.link?.label ?? '';

          return (
            categoryMatches ||
            normalize(question.question).includes(term) ||
            normalize(question.answer).includes(term) ||
            normalize(linkText).includes(term)
          );
        });

        return {
          ...category,
          questions,
        };
      })
      .filter((category) => category.questions.length > 0);
  }, [query]);

  const hasResults = filteredCategories.length > 0;

  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="landing-container">
          <div className="landing-nav">
            <Link to="/" className="landing-logo">
              <img
                src="/assets/ventilio.png"
                alt="Ventilio"
                className="landing-logo-img"
              />
            </Link>

            <button
              type="button"
              className="btn-landing-secondary contact-back-btn"
              onClick={() => navigate('/')}
            >
              <NavArrowLeft width={18} height={18} strokeWidth={2} />
              Retour à l'accueil
            </button>
          </div>
        </div>
      </header>

      <section className="contact-hero">
        <div className="landing-container">
          <h1>Centre d'aide Ventilio</h1>
          <p>Comment pouvons-nous vous aider ?</p>
        </div>
      </section>

      <main className="contact-section">
        <div className="landing-container help-wrap">
          <div className="help-search">
            <Search
              width={20}
              height={20}
              strokeWidth={2}
              className="help-search-icon"
            />

            <input
              type="search"
              className="help-search-input"
              placeholder="Rechercher une question…"
              aria-label="Rechercher une question"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            {query && (
              <button
                type="button"
                className="help-search-clear"
                onClick={() => setQuery('')}
                aria-label="Effacer la recherche"
              >
                <Xmark width={16} height={16} strokeWidth={2} />
              </button>
            )}
          </div>

          {!hasResults && (
            <p className="help-no-results">
              Aucun résultat trouvé. Essayez avec d'autres mots-clés.
            </p>
          )}

          {hasResults && (
            <div className="help-categories">
              {filteredCategories.map((category) => (
                <section key={category.id} className="help-category">
                  <h2 className="help-category-title">
                    {category.title}
                  </h2>

                  <div className="help-accordion">
                    {category.questions.map((question) => {
                      const isOpen = openIds.has(question.id);
                      const panelId = `help-panel-${question.id}`;
                      const buttonId = `help-button-${question.id}`;

                      return (
                        <div key={question.id} className="help-item">
                          <h3 className="help-item-heading">
                            <button
                              type="button"
                              id={buttonId}
                              className="help-item-trigger"
                              aria-expanded={isOpen}
                              aria-controls={panelId}
                              onClick={() => toggleQuestion(question.id)}
                            >
                              <span>{question.question}</span>

                              <NavArrowDown
                                width={18}
                                height={18}
                                strokeWidth={2}
                                className={
                                  `help-item-chevron${
                                    isOpen ? ' help-item-chevron-open' : ''
                                  }`
                                }
                              />
                            </button>
                          </h3>

                          <div
                            id={panelId}
                            role="region"
                            aria-labelledby={buttonId}
                            className="help-item-panel"
                            hidden={!isOpen}
                          >
                            <p>{question.answer}</p>

                            {question.link && (
                              <Link
                                to={question.link.to}
                                className="help-answer-link"
                              >
                                {question.link.label}
                              </Link>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}

          <div className="help-footer-cta">
            <h2>Vous n'avez pas trouvé votre réponse ?</h2>

            <div className="help-footer-cta-actions">
              <Link
                to="/documentation"
                className="btn-landing-secondary"
              >
                Documentation complète
              </Link>

              <Link
                to="/contact"
                className="btn-landing-primary"
              >
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};